import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Calendar, User, Building, ChevronLeft, ChevronRight, AlertCircle, FileText, Trash2, Check, Image, Archive, RotateCcw } from 'lucide-react';
import axios from 'axios';
import AdminNavigation from '../components/AdminNavigation';

const ArchivedSubmissions = () => {
  const navigate = useNavigate();
  const [dashboardState, setDashboardState] = useState({
    loading: true,
    error: null,
    archivedSubmissions: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0
    }
  });

  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null
  });

  const [selectionState, setSelectionState] = useState({
    selectedIds: new Set(),
    isRestoring: false,
    isDeleting: false,
    showRestoreConfirm: false,
    showDeleteConfirm: false
  });

  useEffect(() => {
    checkAuthAndLoadData();
  }, [dashboardState.pagination.page]);

  const checkAuthAndLoadData = async () => {
    try {
      // Check authentication status
      const authResponse = await axios.get('/api/auth/status');
      
      if (!authResponse.data.isAuthenticated) {
        navigate('/admin');
        return;
      }
      
      setAuthState({
        isAuthenticated: true,
        user: authResponse.data.user
      });
      
      // Load archived submissions data
      await loadArchivedSubmissions();
      
    } catch (error) {
      console.error('Auth/data loading error:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      } else {
        setDashboardState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load data. Please refresh the page.'
        }));
      }
    }
  };

  const loadArchivedSubmissions = async () => {
    setDashboardState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await axios.get('/api/archived-submissions', {
        params: {
          page: dashboardState.pagination.page,
          limit: dashboardState.pagination.limit
        }
      });
      
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        archivedSubmissions: response.data.archivedSubmissions,
        pagination: response.data.pagination
      }));
      
    } catch (error) {
      console.error('Load archived submissions error:', error);
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load archived submissions'
      }));
    }
  };

  const handlePageChange = (newPage) => {
    setDashboardState(prev => ({
      ...prev,
      pagination: { ...prev.pagination, page: newPage }
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'Not provided';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Selection handlers
  const handleSelectSubmission = (submissionId, checked) => {
    setSelectionState(prev => {
      const newSelectedIds = new Set(prev.selectedIds);
      if (checked) {
        newSelectedIds.add(submissionId);
      } else {
        newSelectedIds.delete(submissionId);
      }
      return { ...prev, selectedIds: newSelectedIds };
    });
  };

  const handleSelectAll = (checked) => {
    setSelectionState(prev => {
      if (checked) {
        const allIds = new Set(dashboardState.archivedSubmissions.map(s => s.id));
        return { ...prev, selectedIds: allIds };
      } else {
        return { ...prev, selectedIds: new Set() };
      }
    });
  };

  const handleRestoreConfirm = () => {
    setSelectionState(prev => ({ ...prev, showRestoreConfirm: true }));
  };

  const handleRestoreCancel = () => {
    setSelectionState(prev => ({ ...prev, showRestoreConfirm: false }));
  };

  const handleDeleteConfirm = () => {
    setSelectionState(prev => ({ ...prev, showDeleteConfirm: true }));
  };

  const handleDeleteCancel = () => {
    setSelectionState(prev => ({ ...prev, showDeleteConfirm: false }));
  };

  const handleBulkRestore = async () => {
    if (selectionState.selectedIds.size === 0) return;

    setSelectionState(prev => ({ ...prev, isRestoring: true }));

    try {
      const idsArray = Array.from(selectionState.selectedIds);
      
      // Restore submissions one by one
      for (const id of idsArray) {
        await axios.post(`/api/archived-submissions/${id}/restore`);
      }

      // Clear selection and reload data
      setSelectionState({
        selectedIds: new Set(),
        isRestoring: false,
        isDeleting: false,
        showRestoreConfirm: false,
        showDeleteConfirm: false
      });

      // Reload archived submissions
      await loadArchivedSubmissions();

    } catch (error) {
      console.error('Restore error:', error);
      setDashboardState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to restore submission(s)'
      }));
      setSelectionState(prev => ({ ...prev, isRestoring: false, showRestoreConfirm: false }));
    }
  };

  const handleBulkDelete = async () => {
    if (selectionState.selectedIds.size === 0) return;

    setSelectionState(prev => ({ ...prev, isDeleting: true }));

    try {
      const idsArray = Array.from(selectionState.selectedIds);
      
      // Delete archived submissions one by one
      for (const id of idsArray) {
        await axios.delete(`/api/archived-submissions/${id}`);
      }

      // Clear selection and reload data
      setSelectionState({
        selectedIds: new Set(),
        isRestoring: false,
        isDeleting: false,
        showRestoreConfirm: false,
        showDeleteConfirm: false
      });

      // Reload archived submissions
      await loadArchivedSubmissions();

    } catch (error) {
      console.error('Delete error:', error);
      setDashboardState(prev => ({
        ...prev,
        error: error.response?.data?.error || 'Failed to delete archived submission(s)'
      }));
      setSelectionState(prev => ({ ...prev, isDeleting: false, showDeleteConfirm: false }));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout request fails
      navigate('/admin');
    }
  };

  // Reset selection when page changes
  useEffect(() => {
    setSelectionState(prev => ({ ...prev, selectedIds: new Set() }));
  }, [dashboardState.pagination.page]);

  if (dashboardState.loading && dashboardState.archivedSubmissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading archived submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <AdminNavigation user={authState.user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Archived Submissions</h2>
          <p className="mt-2 text-sm text-gray-600">
            View and manage archived form submissions. Restore submissions or permanently delete them.
          </p>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Archive className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Archived</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.pagination.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Page</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardState.archivedSubmissions.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <User className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Page {dashboardState.pagination.page}</p>
                <p className="text-2xl font-bold text-gray-900">of {dashboardState.pagination.pages}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {dashboardState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{dashboardState.error}</p>
                <button
                  onClick={() => loadArchivedSubmissions()}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Action Toolbar */}
        {selectionState.selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-800">
                  {selectionState.selectedIds.size} archived submission{selectionState.selectedIds.size > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectionState(prev => ({ ...prev, selectedIds: new Set() }))}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Clear selection
                </button>
                <button
                  onClick={handleRestoreConfirm}
                  disabled={selectionState.isRestoring}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  {selectionState.isRestoring ? 'Restoring...' : 'Restore Selected'}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={selectionState.isDeleting}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {selectionState.isDeleting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Archived Submissions Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Archived Submissions
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                List of all archived form submissions. You can restore or permanently delete them.
              </p>
            </div>
            {dashboardState.archivedSubmissions.length > 0 && (
              <div className="text-sm text-gray-500">
                Select submissions to restore or permanently delete multiple entries
              </div>
            )}
          </div>
          
          {dashboardState.archivedSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No archived submissions</h3>
              <p className="mt-1 text-sm text-gray-500">
                No form submissions have been archived yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={
                          dashboardState.archivedSubmissions.length > 0 &&
                          selectionState.selectedIds.size === dashboardState.archivedSubmissions.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archive Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PDF Report
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Images
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardState.archivedSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className={`hover:bg-gray-50 ${selectionState.selectedIds.has(submission.id) ? 'bg-orange-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={selectionState.selectedIds.has(submission.id)}
                          onChange={(e) => handleSelectSubmission(submission.id, e.target.checked)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{submission.original_id ? submission.original_id.substring(0, 8) : submission.id.substring(0, 8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {truncateText(submission.company, 30)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {truncateText(submission.contact_person, 20)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {truncateText(submission.email, 25)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <Archive className="h-3 w-3 text-orange-500 mr-1" />
                            <span className="text-xs">
                              {formatDate(submission.archived_at)}
                            </span>
                          </div>
                          {submission.archived_by && (
                            <div className="text-xs text-gray-500 mt-1">
                              by {submission.archived_by.username || `${submission.archived_by.firstName} ${submission.archived_by.lastName}`.trim()}
                            </div>
                          )}
                          {submission.archive_reason && (
                            <div className="text-xs text-gray-600 mt-1 italic">
                              "{truncateText(submission.archive_reason, 30)}"
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {submission.oil_analysis_pdf ? (
                          <div className="flex items-center">
                            <div className="flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                              <FileText className="h-3 w-3 mr-1" />
                              Available
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              <span className="h-3 w-3 mr-1 rounded-full bg-gray-400"></span>
                              Not uploaded
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {submission.submission_images && submission.submission_images.length > 0 ? (
                          <div className="flex items-center">
                            <div className="flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                              <Image className="h-3 w-3 mr-1" />
                              {submission.submission_images.length} image{submission.submission_images.length > 1 ? 's' : ''}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="flex items-center px-2 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              <span className="h-3 w-3 mr-1 rounded-full bg-gray-400"></span>
                              No images
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/archived-submissions/${submission.id}`)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {dashboardState.pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(dashboardState.pagination.page - 1)}
                disabled={dashboardState.pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(dashboardState.pagination.page + 1)}
                disabled={dashboardState.pagination.page === dashboardState.pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((dashboardState.pagination.page - 1) * dashboardState.pagination.limit) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(dashboardState.pagination.page * dashboardState.pagination.limit, dashboardState.pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{dashboardState.pagination.total}</span>{' '}
                  results
                </p>
              </div>
              
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(dashboardState.pagination.page - 1)}
                    disabled={dashboardState.pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {dashboardState.pagination.page} of {dashboardState.pagination.pages}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(dashboardState.pagination.page + 1)}
                    disabled={dashboardState.pagination.page === dashboardState.pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      {selectionState.showRestoreConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <RotateCcw className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Restore Submissions
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to restore {selectionState.selectedIds.size} archived submission
                  {selectionState.selectedIds.size > 1 ? 's' : ''}? They will be moved back to active submissions.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleRestoreCancel}
                    disabled={selectionState.isRestoring}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkRestore}
                    disabled={selectionState.isRestoring}
                    className="px-4 py-2 bg-green-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {selectionState.isRestoring ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Restoring...
                      </>
                    ) : (
                      'Restore'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {selectionState.showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Permanently Delete Archived Submissions
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to permanently delete {selectionState.selectedIds.size} archived submission
                  {selectionState.selectedIds.size > 1 ? 's' : ''}? This action cannot be undone and all associated files will be removed.
                </p>
                {selectionState.selectedIds.size > 1 && (
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    This will permanently delete multiple archived submissions and their associated files.
                  </p>
                )}
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={selectionState.isDeleting}
                    className="px-4 py-2 bg-gray-100 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={selectionState.isDeleting}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {selectionState.isDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete Permanently'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedSubmissions;
